import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";

const PAID_OPTS = [{ v: "unset", l: "Unclassified" }, { v: "paid", l: "Paid" }, { v: "unpaid", l: "Unpaid" }];
const paidToStr = (p) => (p === true ? "paid" : p === false ? "unpaid" : "unset");
const strToPaid = (s) => (s === "paid" ? true : s === "unpaid" ? false : null);

export default function LeaveTypesTab({ reloadTypes }) {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);

  const load = () => api.get("/hr/leave/types").then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const blank = { code: "", name: "", paid: "unset", active: true, requires_attachment: false, allow_half_day: true, allow_negative_balance: false, color: "" };

  return (
    <div data-testid="leave-types-tab">
      <div className="flex justify-end mb-3">
        <Button size="sm" className="bg-slate-900" data-testid="leave-type-add" onClick={() => setEdit({ ...blank, _new: true })}><Plus className="w-4 h-4 mr-1.5" />New Leave Type</Button>
      </div>
      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="excel-table w-full" data-testid="leave-types-table">
          <thead><tr><th>Code</th><th>Name</th><th>Paid</th><th>Half Day</th><th>Attachment</th><th>Neg. Balance</th><th>Active</th><th>Source</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={9} className="text-center text-slate-400 py-6">No leave types — import from AutoCount or add manually</td></tr>
              : rows.map((t) => (
                <tr key={t.id} data-testid={`leave-type-row-${t.code}`}>
                  <td className="font-mono text-xs">{t.code}</td>
                  <td>{t.name}</td>
                  <td className="text-xs">{t.paid === true ? "Paid" : t.paid === false ? "Unpaid" : "—"}</td>
                  <td className="text-center">{t.allow_half_day ? "✓" : "—"}</td>
                  <td className="text-center">{t.requires_attachment ? "Required" : "—"}</td>
                  <td className="text-center">{t.allow_negative_balance ? "Allowed" : "Blocked"}</td>
                  <td className="text-center">{t.active ? <Badge variant="outline" className="text-emerald-700 border-emerald-300">Active</Badge> : <Badge variant="outline" className="text-slate-500">Inactive</Badge>}</td>
                  <td className="text-xs text-slate-400">{t.source}{t.external_autocount_ref ? ` (${t.external_autocount_ref})` : ""}</td>
                  <td><Button size="sm" variant="ghost" data-testid={`leave-type-edit-${t.code}`} onClick={() => setEdit({ ...t, paid: paidToStr(t.paid) })}><Pencil className="w-3.5 h-3.5" /></Button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {edit && <TypeDialog edit={edit} setEdit={setEdit} onDone={() => { load(); reloadTypes?.(); }} />}
    </div>
  );
}

function TypeDialog({ edit, setEdit, onDone }) {
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setEdit({ ...edit, [k]: v });
  const submit = async () => {
    if (!edit.code?.trim() || !edit.name?.trim()) return toast.error("Code and name are required");
    setBusy(true);
    const body = { code: edit.code, name: edit.name, paid: strToPaid(edit.paid), active: edit.active,
      requires_attachment: edit.requires_attachment, allow_half_day: edit.allow_half_day,
      allow_negative_balance: edit.allow_negative_balance, color: edit.color || "" };
    try {
      if (edit._new) await api.post("/hr/leave/types", body);
      else await api.put(`/hr/leave/types/${edit.id}`, body);
      toast.success("Saved"); setEdit(null); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const Row = ({ k, label }) => (
    <label className="flex items-center gap-2 text-sm"><Checkbox checked={edit[k]} onCheckedChange={(v) => set(k, !!v)} data-testid={`type-${k}`} />{label}</label>
  );
  return (
    <Dialog open onOpenChange={(v) => !v && setEdit(null)}>
      <DialogContent className="max-w-md" data-testid="leave-type-dialog">
        <DialogHeader><DialogTitle>{edit._new ? "New Leave Type" : `Edit ${edit.code}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Code</Label><Input value={edit.code} onChange={(e) => set("code", e.target.value)} data-testid="type-code" /></div>
            <div><Label className="text-xs">Paid / Unpaid</Label>
              <Select value={edit.paid} onValueChange={(v) => set("paid", v)}><SelectTrigger data-testid="type-paid"><SelectValue /></SelectTrigger>
                <SelectContent>{PAID_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div><Label className="text-xs">Name</Label><Input value={edit.name} onChange={(e) => set("name", e.target.value)} data-testid="type-name" /></div>
          <div className="space-y-2 pt-1">
            <Row k="active" label="Active" />
            <Row k="allow_half_day" label="Allow Half Day" />
            <Row k="requires_attachment" label="Requires Attachment" />
            <Row k="allow_negative_balance" label="Allow Negative Balance" />
          </div>
          {edit.external_autocount_ref && <p className="text-[11px] text-slate-400">Source reference: AutoCount {edit.external_autocount_ref} (preserved)</p>}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button><Button onClick={submit} disabled={busy} data-testid="type-submit" className="bg-slate-900">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
